import { subscribeToGlobalEvent } from "@storytell/events";
import type { ProjectId } from "@storytell/types";
import { createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import { useWire } from "~/wire";

/**
 * Hook that answers "can this URL's project be rendered?"
 *
 * The URL is the source of truth for the active project — `activeContextStore`
 * refetches whenever `:projectId` changes, so there's no client-side context
 * to "sync" anymore. This hook only tracks access denials (403/401) surfaced
 * by the fetch interceptor and provides a convenience `showProjectNoAccess`
 * flag the layout reads to render the "no access" fallback.
 */
export const useProjectUrlSync = () => {
  const wire = useWire();
  const nav = wire.navigation;

  const [deniedProjectId, setDeniedProjectId] = createSignal<string | undefined>(undefined);

  createEffect(() => {
    const unsubDenied = subscribeToGlobalEvent("project-access-denied", (event) => {
      const projectId = event.detail?.projectId;
      if (projectId) setDeniedProjectId(projectId);
    });
    const unsubOk = subscribeToGlobalEvent("project-access-ok", (event) => {
      const projectId = event.detail?.projectId;
      if (projectId === deniedProjectId()) setDeniedProjectId(undefined);
    });
    onCleanup(() => {
      unsubDenied();
      unsubOk();
    });
  });

  const urlProjectId = () => nav.state().projectId as ProjectId | undefined;

  createEffect(() => {
    const urlProjId = urlProjectId();
    const denied = deniedProjectId();
    if (denied != null && urlProjId !== denied) {
      setDeniedProjectId(undefined);
    }
  });

  const projectsLoaded = () => (wire.projects?.all?.() ?? []).length > 0;

  // Guests only see the banner once the server actively denies them; otherwise
  // they'd fail the default-project flow. Logged-in users see it whenever the
  // URL project is not in their loaded list.
  const showProjectNoAccess = createMemo(() => {
    const urlProjId = urlProjectId();
    const isGuest = wire.auth?.isGuest?.();
    const loaded = projectsLoaded();
    const projectInList = urlProjId ? !!wire.projects?.get?.(urlProjId) : false;

    if (!urlProjId) return false;
    if (isGuest) return deniedProjectId() === urlProjId;
    if (!loaded) return false;
    return !projectInList;
  });

  return {
    /** Always true now that the URL is source of truth — kept for API compat. */
    isSynced: () => true,
    showProjectNoAccess,
  };
};
